import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage } from './errors'
import { http } from './api'
import type { CreateOAuthClientInput, CreateOAuthClientResult, OAuthClientListItem } from './oauth-types'

export interface OAuthApi {
  clients(): Promise<OAuthClientListItem[]>
  createClient(input: CreateOAuthClientInput): Promise<CreateOAuthClientResult>
  revokeClient(id: string): Promise<void>
  reactivateClient(id: string): Promise<OAuthClientListItem>
}

class RealOAuthApi implements OAuthApi {
  async clients() {
    const res = await http.get<{ clients: OAuthClientListItem[] }>('/api/me/oauth-clients')
    return res.clients
  }
  createClient(input: CreateOAuthClientInput) {
    return http.post<CreateOAuthClientResult>('/api/me/oauth-clients', input)
  }
  revokeClient(id: string) {
    return http.del<void>(`/api/me/oauth-clients/${id}`)
  }
  reactivateClient(id: string) {
    return http.post<OAuthClientListItem>(`/api/me/oauth-clients/${id}/reactivate`)
  }
}

export const oauthApi: OAuthApi = new RealOAuthApi()

export const oauthKeys = {
  clients: ['oauth', 'clients'] as const,
}

export function useOAuthClients() {
  return useQuery({ queryKey: oauthKeys.clients, queryFn: () => oauthApi.clients() })
}

export function useCreateOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOAuthClientInput) => oauthApi.createClient(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthKeys.clients }),
    onError: (e) => toast.error(errorMessage(e, 'Aanmaken mislukt')),
  })
}

export function useRevokeOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => oauthApi.revokeClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.clients })
      toast.success('Koppeling uitgeschakeld')
    },
    onError: (e) => toast.error(errorMessage(e, 'Uitschakelen mislukt')),
  })
}

export function useReactivateOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => oauthApi.reactivateClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.clients })
      toast.success('Koppeling weer ingeschakeld')
    },
    onError: (e) => toast.error(errorMessage(e, 'Inschakelen mislukt')),
  })
}
